/**
 * The file converter pages: select (or drop) files, choose options, press
 * Start!. Each file is uploaded with progress (POST /api/v1/uploads), then a
 * job converts it (POST /api/v1/jobs, polled until done) and the result
 * appears in the Inbox. The PDF Combiner uploads every file first and sends
 * one job with the uploads in the list's order; the order can be changed by
 * drag and drop or with the move buttons (keyboard).
 */

import { trackConversion } from "../analytics";
import { createJob, deleteUpload, uploadFile } from "../api/client";
import type { Me } from "../api/types";
import type { Inbox } from "../inbox";
import { announce } from "../lib/announce";
import { find, get, h } from "../lib/dom";
import { formatSize } from "../lib/format";
import { errorMessage, isAbortError } from "../lib/http";
import { isPremium, loadMe } from "../session";
import { openSignup, recordSuccessfulUpload, shouldNudge } from "../signup";
import { waitForJob } from "./jobs";
import {
  initOptions,
  readFilename,
  readOptions,
  setOptionsDisabled,
} from "./options";
import { UploadQueue, dropIndex } from "./queue";
import type { AddResult, ItemState, QueueItem } from "./queue";

const COMBINE = "combinetopdf";
const UPLOAD_CONCURRENCY = 2;
const DEFAULT_MAX_UPLOAD_BYTES = 128 * 1024 * 1024;
const PERCENT = 100;
const MIB = 1024 * 1024;
const CONVERT_FAILED = "Cannot convert the file.";
const UNSUPPORTED =
  "You have selected one or several files of unsupported formats. These files have not been added.";

/** Run `task` for each item, `limit` at a time (tasks handle their own errors). */
async function runLimited<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      if (item !== undefined) await task(item);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
}

/**
 * Like runLimited, but the first item runs alone: the first request of a
 * visitor creates the browser session (its cookie comes with the answer), and
 * parallel first requests would each create their own session, so files of one
 * batch would end up in different sessions.
 */
async function runFirstAlone<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  const [first, ...rest] = items;
  if (first === undefined) return;
  await task(first);
  await runLimited(rest, limit, task);
}

function stateText(state: ItemState): string {
  switch (state.kind) {
    case "ready":
      return "Ready";
    case "uploading":
      return `Uploading… ${Math.round(state.fraction * PERCENT)}%`;
    case "uploaded":
      return "Uploaded";
    case "converting":
      return state.percent > 0
        ? `Processing… ${state.percent}%`
        : "Processing…";
    case "done":
      return "Done: the file is in your Inbox";
    case "failed":
      return state.message;
  }
}

function progressValue(state: ItemState): number | null {
  if (state.kind === "uploading") return Math.round(state.fraction * PERCENT);
  if (state.kind === "converting") return state.percent;
  return null;
}

class Converter {
  private readonly tool: string;
  private readonly extensions: ReadonlySet<string>;
  private readonly freeFiles: number;
  private readonly queue = new UploadQueue();
  private readonly controllers = new Map<string, AbortController>();
  private visitor: Me | null = null;
  private running = false;
  private dragged: string | null = null;

  private readonly input: HTMLInputElement;
  private readonly selectButton: HTMLButtonElement;
  private readonly panel: HTMLElement;
  private readonly rows: HTMLTableSectionElement;
  private readonly addButton: HTMLButtonElement;
  private readonly startButton: HTMLButtonElement;
  private readonly clearButton: HTMLButtonElement;
  private readonly trialLimit: HTMLElement;
  private readonly formatError: HTMLElement;
  private readonly formatErrorText: HTMLElement;
  private readonly panelError: HTMLElement;
  private readonly root: HTMLElement;
  private readonly inbox: Inbox | null;

  constructor(root: HTMLElement, inbox: Inbox | null) {
    this.root = root;
    this.inbox = inbox;
    this.tool = root.dataset["tool"] ?? "";
    this.extensions = new Set(
      (root.dataset["extensions"] ?? "").split(",").filter((ext) => ext !== ""),
    );
    this.freeFiles = Number.parseInt(root.dataset["freeFiles"] ?? "2", 10);
    this.input = get(root, "[data-file-input]", HTMLInputElement);
    this.selectButton = get(root, "[data-select-files]", HTMLButtonElement);
    this.panel = get(root, "[data-upload-panel]", HTMLElement);
    this.rows = get(root, "[data-queue]", HTMLTableSectionElement);
    this.addButton = get(root, "[data-add-files]", HTMLButtonElement);
    this.startButton = get(root, "[data-start]", HTMLButtonElement);
    this.clearButton = get(root, "[data-clear]", HTMLButtonElement);
    this.trialLimit = get(root, "[data-trial-limit]", HTMLElement);
    this.formatError = get(root, "[data-format-error]", HTMLElement);
    this.formatErrorText = get(root, "[data-format-error-text]", HTMLElement);
    this.panelError = get(root, "[data-panel-error]", HTMLElement);
  }

  private get combine(): boolean {
    return this.tool === COMBINE;
  }

  private get premium(): boolean {
    return isPremium(this.visitor);
  }

  start(): void {
    void loadMe().then((visitor) => {
      this.visitor = visitor;
    });
    initOptions(this.root);
    this.selectButton.addEventListener("click", () => {
      this.requestFiles();
    });
    this.addButton.addEventListener("click", () => {
      this.requestFiles();
    });
    this.input.addEventListener("change", () => {
      this.addFiles([...(this.input.files ?? [])]);
      this.input.value = ""; // the same file can be selected again
    });
    this.startButton.addEventListener("click", () => {
      void this.run();
    });
    this.clearButton.addEventListener("click", () => {
      this.clear();
    });
    this.rows.addEventListener("click", (event) => {
      this.onRowClick(event);
    });
    this.bindDragAndDrop();
  }

  // ------------------------------------------------------------------ adding files

  private requestFiles(): void {
    if (this.running) return;
    if (!this.premium && shouldNudge()) {
      openSignup(() => {
        this.input.click();
      });
      return;
    }
    this.input.click();
  }

  private maxFiles(): number {
    if (!this.premium) return this.freeFiles;
    return this.combine
      ? (this.visitor?.limits.maxCombineFiles ?? this.freeFiles)
      : Number.POSITIVE_INFINITY;
  }

  private maxBytes(): number {
    return this.visitor?.limits.maxUploadBytes ?? DEFAULT_MAX_UPLOAD_BYTES;
  }

  addFiles(files: readonly File[]): void {
    if (this.running || files.length === 0) return;
    const result = this.queue.add(files, {
      extensions: this.extensions,
      maxFiles: this.maxFiles(),
      maxBytes: this.maxBytes(),
    });
    this.showAddProblems(result);
    this.render();
    if (result.added.length > 0) {
      const count = result.added.length;
      announce(
        `${count} ${count === 1 ? "file" : "files"} added. Press Start! to convert.`,
      );
    }
  }

  private showAddProblems(result: AddResult): void {
    const problems: string[] = [];
    if (result.unsupported.length > 0) problems.push(UNSUPPORTED);
    if (result.tooLarge.length > 0) {
      const limit = Math.round(this.maxBytes() / MIB);
      problems.push(
        `Files larger than ${limit} MB cannot be converted: ${result.tooLarge.join(", ")}.`,
      );
    }
    this.formatErrorText.textContent = problems.join(" ");
    this.formatError.hidden = problems.length === 0;
    const overLimit = result.overLimit.length > 0;
    this.trialLimit.hidden = !(overLimit && !this.premium);
    if (overLimit && this.premium) {
      this.setPanelError(
        `You can combine up to ${this.maxFiles()} files at once.`,
      );
    } else {
      this.setPanelError("");
    }
    if (problems.length > 0 || overLimit) {
      announce(
        [...problems, overLimit ? "Some files have not been added." : ""].join(
          " ",
        ),
      );
    }
  }

  private setPanelError(message: string): void {
    this.panelError.textContent = message;
    this.panelError.hidden = message === "";
  }

  // ------------------------------------------------------------------ the list

  private render(): void {
    const focused = document.activeElement;
    const focusKey =
      focused instanceof HTMLElement && this.rows.contains(focused)
        ? { key: focused.dataset["key"], action: focused.dataset["action"] }
        : null;
    const items = this.queue.items;
    this.rows.replaceChildren(
      ...items.map((item, index) => this.row(item, index, items.length)),
    );
    this.panel.hidden =
      items.length === 0 && this.formatError.hidden && this.trialLimit.hidden;
    this.startButton.disabled =
      this.running || this.queue.runnable.length === 0;
    this.addButton.disabled = this.running;
    this.selectButton.disabled = this.running;
    this.clearButton.hidden =
      this.running || !items.some((item) => item.state.kind === "done");
    setOptionsDisabled(this.root, this.running);
    if (focusKey?.key !== undefined)
      this.restoreFocus(focusKey.key, focusKey.action);
  }

  private restoreFocus(key: string, action: string | undefined): void {
    const candidates = [action, "up", "down", "remove"].filter(
      (name) => name !== undefined,
    );
    for (const name of candidates) {
      const button = find(
        this.rows,
        `[data-key="${CSS.escape(key)}"][data-action="${name}"]`,
        HTMLButtonElement,
      );
      if (button !== null && !button.disabled) {
        button.focus();
        return;
      }
    }
    this.addButton.focus();
  }

  private row(
    item: QueueItem,
    index: number,
    total: number,
  ): HTMLTableRowElement {
    const name = item.file.name;
    const value = progressValue(item.state);
    const status = h(
      "td",
      { className: `queue__status queue__status--${item.state.kind}` },
      ...(item.state.kind === "failed"
        ? [
            h("span", {
              className: "alert__icon",
              text: "!",
              attrs: { "aria-hidden": "true" },
            }),
          ]
        : []),
      h("span", { text: stateText(item.state) }),
      ...(value === null
        ? []
        : [
            h("progress", {
              attrs: {
                max: "100",
                value: String(value),
                "aria-label": `${name} progress`,
              },
            }),
          ]),
    );
    const actions = h("td", { className: "queue__actions" });
    if (this.combine) {
      actions.append(
        this.button(
          item,
          "up",
          "↑",
          `Move ${name} up`,
          this.running || index === 0,
        ),
        this.button(
          item,
          "down",
          "↓",
          `Move ${name} down`,
          this.running || index === total - 1,
        ),
      );
    }
    actions.append(
      this.button(
        item,
        "remove",
        "×",
        `Remove ${name}`,
        this.running && this.combine,
      ),
    );
    const row = h(
      "tr",
      { attrs: { "data-key": item.key } },
      h("td", { className: "queue__number", text: String(index + 1) }),
      h(
        "td",
        { className: "queue__name" },
        h("span", { text: name, attrs: { title: name } }),
      ),
      h("td", { className: "queue__size", text: formatSize(item.file.size) }),
      status,
      actions,
    );
    if (this.combine && !this.running) {
      row.draggable = true;
      row.classList.add("queue__row--draggable");
    }
    return row;
  }

  private button(
    item: QueueItem,
    action: string,
    symbol: string,
    label: string,
    disabled: boolean,
  ): HTMLButtonElement {
    const button = h("button", {
      className: "icon-button",
      text: symbol,
      attrs: {
        type: "button",
        "data-key": item.key,
        "data-action": action,
        "aria-label": label,
      },
    });
    button.disabled = disabled;
    return button;
  }

  private onRowClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest("button[data-action]");
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const key = button.dataset["key"] ?? "";
    const action = button.dataset["action"];
    if (action === "up") this.move(key, -1);
    else if (action === "down") this.move(key, 1);
    else if (action === "remove") this.remove(key);
  }

  private move(key: string, delta: number): void {
    if (this.running || !this.queue.move(key, delta)) return;
    this.render();
    this.announcePosition(key);
  }

  private announcePosition(key: string): void {
    const item = this.queue.find(key);
    if (item === undefined) return;
    announce(
      `${item.file.name} moved to position ${this.queue.indexOf(key) + 1} of ${this.queue.items.length}.`,
    );
  }

  private remove(key: string): void {
    this.controllers.get(key)?.abort();
    const item = this.queue.remove(key);
    if (item !== undefined && item.uploadId !== null) {
      // the stored upload expires on its own; deleting it now only frees space early
      void deleteUpload(item.uploadId).catch(() => undefined);
    }
    if (this.queue.items.length === 0) {
      this.trialLimit.hidden = true;
      this.formatError.hidden = true;
      this.setPanelError("");
    }
    this.render();
    if (item) announce(`${item.file.name} removed.`);
    if (this.queue.items.length === 0) this.selectButton.focus();
  }

  private clear(): void {
    for (const item of this.queue.clear()) {
      this.controllers.get(item.key)?.abort();
    }
    this.trialLimit.hidden = true;
    this.formatError.hidden = true;
    this.setPanelError("");
    this.render();
    this.selectButton.focus();
  }

  private bindDragAndDrop(): void {
    // files dropped anywhere on the converter box are added
    this.root.addEventListener("dragover", (event) => {
      if (
        event.dataTransfer?.types.includes("Files") === true ||
        this.dragged !== null
      ) {
        event.preventDefault();
      }
    });
    this.root.addEventListener("drop", (event) => {
      const files = event.dataTransfer?.files;
      if (this.dragged === null && files !== undefined && files.length > 0) {
        event.preventDefault();
        this.addFiles([...files]);
      }
    });
    // rows of the combiner are reordered by dragging
    this.rows.addEventListener("dragstart", (event) => {
      const row =
        event.target instanceof Element ? event.target.closest("tr") : null;
      const key = row?.dataset["key"];
      if (
        !this.combine ||
        this.running ||
        key === undefined ||
        event.dataTransfer === null
      )
        return;
      this.dragged = key;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", key);
      row?.classList.add("queue__row--dragging");
    });
    this.rows.addEventListener("dragover", (event) => {
      if (this.dragged === null) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    });
    this.rows.addEventListener("drop", (event) => {
      const key = this.dragged;
      const row =
        event.target instanceof Element ? event.target.closest("tr") : null;
      const over = row?.dataset["key"];
      if (key === null || row === null || over === undefined) return;
      event.preventDefault();
      const box = row.getBoundingClientRect();
      const after = event.clientY > box.top + box.height / 2;
      const index = dropIndex(
        this.queue.indexOf(key),
        this.queue.indexOf(over),
        after,
      );
      if (this.queue.moveTo(key, index)) {
        this.render();
        this.announcePosition(key);
      }
    });
    this.rows.addEventListener("dragend", () => {
      this.dragged = null;
      for (const row of this.rows.querySelectorAll(".queue__row--dragging")) {
        row.classList.remove("queue__row--dragging");
      }
    });
  }

  // ------------------------------------------------------------------ converting

  private update(item: QueueItem, state: ItemState): void {
    const previous = item.state.kind;
    item.state = state;
    if (this.queue.find(item.key) === undefined) return; // removed meanwhile
    this.render();
    if (state.kind !== previous) {
      if (state.kind === "done")
        announce(`${item.file.name} converted. The result is in your Inbox.`);
      if (state.kind === "failed")
        announce(`${item.file.name}: ${state.message}`);
    }
  }

  private setRunning(running: boolean): void {
    const focused = document.activeElement;
    this.running = running;
    this.panel.setAttribute("aria-busy", String(running));
    this.render();
    // a control that just got disabled loses the focus: keep it in the panel
    if (focused instanceof HTMLButtonElement && focused.disabled) {
      this.panel.focus();
    }
  }

  private async run(): Promise<void> {
    if (this.running || this.queue.runnable.length === 0) return;
    this.setPanelError("");
    if (this.combine && this.queue.unfinished.length < 2) {
      this.setPanelError("Please add at least two files to combine.");
      return;
    }
    this.setRunning(true);
    try {
      if (this.combine) {
        await this.runCombine();
      } else {
        const items = this.queue.runnable;
        const [first] = items;
        if (first !== undefined && items.length > 1)
          await this.uploadFirst(first);
        await runLimited(items, UPLOAD_CONCURRENCY, (item) =>
          this.convertOne(item),
        );
      }
    } finally {
      this.setRunning(false);
    }
  }

  /**
   * Upload the first file of a batch on its own before the others start: its
   * answer brings the browser-session cookie, which every later request needs
   * (parallel first requests would each create a session of their own). A
   * failure is left to convertOne, which retries the upload and reports it.
   */
  private async uploadFirst(item: QueueItem): Promise<void> {
    const controller = new AbortController();
    this.controllers.set(item.key, controller);
    try {
      await this.upload(item, controller.signal);
    } catch {
      // reported by convertOne
    } finally {
      this.controllers.delete(item.key);
    }
  }

  /** Upload a file unless it is uploaded already. */
  private async upload(item: QueueItem, signal: AbortSignal): Promise<void> {
    if (item.uploadId !== null) return;
    this.update(item, { kind: "uploading", fraction: 0 });
    const upload = await uploadFile(item.file, {
      signal,
      onProgress: (fraction) => {
        this.update(item, { kind: "uploading", fraction });
      },
    });
    item.uploadId = upload.id;
    if (!this.premium) recordSuccessfulUpload();
  }

  private async convertOne(item: QueueItem): Promise<void> {
    const controller = new AbortController();
    this.controllers.set(item.key, controller);
    try {
      await this.upload(item, controller.signal);
      const uploadId = item.uploadId;
      if (uploadId === null) return;
      this.update(item, { kind: "converting", percent: 0 });
      const job = await createJob(
        {
          tool: this.tool,
          uploads: [uploadId],
          options: readOptions(this.root),
        },
        controller.signal,
      );
      item.uploadId = null; // the job owns the upload now; a retry uploads again
      const done = await waitForJob(job.id, {
        signal: controller.signal,
        onUpdate: (update) => {
          this.update(item, { kind: "converting", percent: update.progress });
        },
      });
      if (done.status === "succeeded") {
        trackConversion(this.tool);
        this.update(item, { kind: "done" });
        void this.inbox?.refresh();
      } else {
        this.update(item, {
          kind: "failed",
          message: done.error?.message ?? CONVERT_FAILED,
        });
      }
    } catch (error) {
      if (isAbortError(error)) return; // removed by the visitor
      this.update(item, { kind: "failed", message: errorMessage(error) });
    } finally {
      this.controllers.delete(item.key);
    }
  }

  private async runCombine(): Promise<void> {
    const controller = new AbortController();
    const items = this.queue.unfinished;
    for (const item of items) this.controllers.set(item.key, controller);
    try {
      await runFirstAlone(items, UPLOAD_CONCURRENCY, async (item) => {
        try {
          await this.upload(item, controller.signal);
          this.update(item, { kind: "uploaded" });
        } catch (error) {
          if (isAbortError(error)) throw error;
          this.update(item, { kind: "failed", message: errorMessage(error) });
        }
      });
      const uploads = items.map((item) => item.uploadId);
      if (!uploads.every((id): id is string => id !== null)) {
        this.setPanelError(
          "Some files could not be uploaded. Remove them or press Start! to try again; nothing was combined.",
        );
        return;
      }
      for (const item of items)
        this.update(item, { kind: "converting", percent: 0 });
      const job = await createJob(
        {
          tool: this.tool,
          uploads,
          options: {},
          filename: readFilename(this.root),
        },
        controller.signal,
      );
      for (const item of items) item.uploadId = null;
      const done = await waitForJob(job.id, {
        signal: controller.signal,
        onUpdate: (update) => {
          for (const item of items)
            this.update(item, { kind: "converting", percent: update.progress });
        },
      });
      if (done.status === "succeeded") {
        trackConversion(this.tool);
        for (const item of items) item.state = { kind: "done" };
        announce("The files were combined. The PDF is in your Inbox.");
        void this.inbox?.refresh();
      } else {
        const message = done.error?.message ?? "Unable to combine the files.";
        for (const item of items) item.state = { kind: "failed", message };
        this.setPanelError(message);
      }
    } catch (error) {
      if (isAbortError(error)) return;
      const message = errorMessage(error);
      for (const item of items) {
        if (item.state.kind !== "failed")
          item.state = { kind: "failed", message };
      }
      this.setPanelError(message);
    } finally {
      for (const item of items) this.controllers.delete(item.key);
    }
  }
}

/** Start the converter of the page, if it has one. */
export function initConverter(inbox: Inbox | null): void {
  const root = find(document, "[data-converter]", HTMLElement);
  if (root === null) return;
  new Converter(root, inbox).start();
}
